import React, { useEffect, useState } from "react";
import Modal from "../../components/Modal/Modal";
import Navigation from "../../components/Navigation/Navigation";
import "./menuEditor.css";
import add from "../../assets/add.png";
import image from "../../assets/image.png";
import { useParams } from "react-router";
import { request } from "../../utils/axios-utils";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import noImage from "../../assets/no_image.png";

const MenuEditor = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState("");
  const [languageList, setLanguageList] = useState([]);
  const [activeModal, setActiveModal] = useState("");
  const [error, setError] = useState("");
  const [mainCategories, setMainCategories] = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    type: "",
    id: "",
  });

  const [categoryData, setCategoryData] = useState({
    selectedNameLang: "",
    name: [],
    image: null,
    available: true,
  });
  const [editCategoryData, setEditCategoryData] = useState({});

  const addCategory = async () => {
    setError("");

    const mainLanguage = languageList[0]; // Assuming the main language is the first in the list.
    const mainLanguageName = categoryData.name[mainLanguage];

    // Ensure the main language name is provided
    if (!mainLanguageName) {
      setError(`Please provide the category name in ${mainLanguage}.`);
      return;
    }

    // Automatically translate missing languages
    const nameTranslations = { ...categoryData.name }; // Start with the existing names
    languageList.forEach((lang) => {
      if (!nameTranslations[lang] && lang !== mainLanguage) {
        nameTranslations[lang] = ""; // Mark missing translations
      }
    });

    // Assign the updated names back to `categoryData`
    const updatedCategoryData = {
      ...categoryData,
      name: nameTranslations,
    };

    try {
      setLoading("addCategory");
      const formData = new FormData();
      formData.append("image", updatedCategoryData.image);
      formData.append("categoryData", JSON.stringify(updatedCategoryData));
      formData.append("slug", JSON.stringify(slug));
      formData.append("parentId", JSON.stringify(null));

      const response = await request({
        url: "/member/add-category",
        method: "POST",
        data: formData,
      });

      // Add the new category to the list and reset the form
      setMainCategories((prevData) => [...prevData, response.data.newCategory]);

      toast.success(response.data.message);

      const languages = languageList.reduce((acc, lang) => {
        acc[lang] = "";
        return acc;
      }, {});
      setCategoryData({
        name: languages,
        description: languages,
        selectedNameLang: mainLanguage,
        image: null,
        available: true,
      });
      setError("");
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
      setActiveModal("");
    }
  };

  const editCategory = async () => {
    setError("");

    // Check if the main language is provided
    const mainLanguage = languageList[0]; // Assuming the first language is the main language
    if (!editCategoryData.name[mainLanguage]) {
      setError(
        `Please enter the name for the main language (${mainLanguage}).`
      );
      return;
    }

    try {
      setLoading("editCategory");
      const formData = new FormData();

      if (editCategoryData.newImage) {
        formData.append("image", editCategoryData.newImage);
      }
      formData.append("editCategoryData", JSON.stringify(editCategoryData));
      formData.append("slug", JSON.stringify(slug));

      const response = await request({
        url: "/member/edit-category",
        method: "POST",
        data: formData,
      });

      setMainCategories((prevData) =>
        prevData.map((category) =>
          category.id === response.data.category.id
            ? response.data.category
            : category
        )
      );

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error editing category.");
      setActiveModal("");
    } finally {
      setLoading("");
      setActiveModal("");
      setEditCategoryData({});
    }
  };

  const deleteCategory = async () => {
    try {
      setLoading("deleteCategory");

      const response = await request({
        url: "/member/delete-category",
        method: "POST",
        data: { categoryId: deleteModal.id, slug },
      });

      setMainCategories((prevData) =>
        prevData.filter((category) => category.id !== deleteModal.id)
      );

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
    }
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading("data");

        //Main Categories
        const categoriesResponse = await request({
          url: "/member/get-main-categories",
          method: "POST",
          data: { slug },
        });
        setMainCategories(categoriesResponse.data.mainCategories || []);

        //LanguageList
        const response = await request({
          url: "/member/get-languages",
          method: "POST",
          data: { slug },
        });

        const languages = response.data.languages.reduce((acc, lang) => {
          acc[lang] = "";
          return acc;
        }, {});

        setLanguageList(response.data.languages);
        setCategoryData((prevData) => ({
          ...prevData,
          name: languages,
          description: languages,
          selectedNameLang: response.data.languages[0],
          selectedDescriptionLang: response.data.languages[0],
        }));
      } catch (error) {
        toast.error(error.response.data.message);
      } finally {
        setLoading("");
      }
    };

    getData();
  }, []);

  return (
    <Navigation isLoading={loading === "data"}>
      {activeModal === "addCategory" && (
        <Modal
          heading={"Add Category"}
          paragraph={"Enter information about your category."}
          closeModal={() => {
            setActiveModal("");
            const languages = languageList.reduce((acc, lang) => {
              acc[lang] = "";
              return acc;
            }, {});
            setCategoryData({
              name: languages,
              description: languages,
              selectedNameLang: languageList[0],
              image: null,
              available: true,
            });
            setError("");
          }}
          btnText={"Add"}
          isLoading={loading === "addCategory"}
          btnFuntion={loading === "addCategory" ? null : addCategory}
        >
          <div className="modal-label-div">
            <label htmlFor="categoryName" className="modal-label">
              Category Name *
            </label>

            <select
              className="language-select"
              onChange={(e) => {
                const selectedLanguage = e.target.value;
                setCategoryData((prevData) => {
                  return { ...prevData, selectedNameLang: selectedLanguage };
                });
              }}
            >
              {languageList.map((language) => {
                return <option key={language}>{language}</option>;
              })}
            </select>
          </div>
          <input
            id="categoryName"
            value={categoryData.name[categoryData.selectedNameLang]}
            onChange={(e) => {
              setCategoryData((prevData) => ({
                ...prevData,
                name: {
                  ...prevData.name,
                  [prevData.selectedNameLang]: e.target.value,
                },
              }));
            }}
            name="name"
            placeholder="Category name"
            className="classic-input mb-16"
            maxLength={30}
          />

          <div className="image-remove-p-div">
            <p
              onClick={() => {
                setCategoryData((prevData) => ({
                  ...prevData,

                  image: null,
                }));
              }}
              className="image-remove-p"
            >
              Remove Image
            </p>
          </div>
          <div className="image-input-div">
            <div
              className={
                categoryData.image ? "image-input border-none" : "image-input"
              }
            >
              {categoryData.image ? (
                <>
                  <img
                    className="image-output"
                    src={
                      categoryData.image.url
                        ? categoryData.image.url
                        : URL.createObjectURL(categoryData.image)
                    }
                  />
                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setCategoryData((prevState) => ({
                          ...prevState,
                          image: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              ) : (
                <>
                  <img className="image-placeholder" src={image} />

                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setCategoryData((prevState) => ({
                          ...prevState,
                          image: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              )}
            </div>
          </div>

          <div className="checkbox-container">
            <input
              checked={categoryData.available}
              onChange={(e) => {
                setCategoryData((prevData) => ({
                  ...prevData,
                  available: e.target.checked,
                }));
              }}
              className="checkbox-input"
              type="checkbox"
              id="customCheckbox"
            />
            <label htmlFor="customCheckbox" className="custom-checkbox"></label>
            <p className="checkbox-p">Available</p>
          </div>

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {activeModal === "editCategory" && (
        <Modal
          heading={"Edit Category"}
          paragraph={"Edit information about your category."}
          closeModal={() => {
            setActiveModal("");
            setEditCategoryData({});
            setError("");
          }}
          btnText={"Save"}
          isLoading={loading === "editCategory"}
          btnFuntion={loading === "editCategory" ? null : editCategory}
        >
          <div className="modal-label-div">
            <label htmlFor="categoryName" className="modal-label">
              Category Name *
            </label>

            <select
              className="language-select"
              onChange={(e) => {
                const selectedLanguage = e.target.value;
                setEditCategoryData((prevData) => {
                  return { ...prevData, selectedNameLang: selectedLanguage };
                });
              }}
            >
              {languageList.map((language) => {
                return <option key={language}>{language}</option>;
              })}
            </select>
          </div>
          <input
            id="categoryName"
            value={editCategoryData.name[editCategoryData.selectedNameLang]}
            onChange={(e) => {
              setEditCategoryData((prevData) => ({
                ...prevData,
                name: {
                  ...prevData.name,
                  [prevData.selectedNameLang]: e.target.value,
                },
              }));
            }}
            name="name"
            placeholder="Category name"
            className="classic-input mb-16"
            maxLength={30}
          />

          <div className="image-remove-p-div">
            <p
              onClick={() => {
                setEditCategoryData((prevData) => ({
                  ...prevData,
                  newImage: null,
                  image: null,
                }));
              }}
              className="image-remove-p"
            >
              Remove Image
            </p>
          </div>
          <div className="image-input-div">
            <div
              className={
                !editCategoryData?.image && !editCategoryData.newImage
                  ? "image-input"
                  : "image-input border-none"
              }
            >
              {editCategoryData.image || editCategoryData.newImage ? (
                <>
                  <img
                    className="image-output"
                    src={
                      editCategoryData.newImage
                        ? URL.createObjectURL(editCategoryData.newImage)
                        : editCategoryData.image.url
                    }
                  />
                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setEditCategoryData((prevState) => ({
                          ...prevState,
                          newImage: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              ) : (
                <>
                  <img className="image-placeholder" src={image} />

                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setEditCategoryData((prevState) => ({
                          ...prevState,
                          newImage: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              )}
            </div>
          </div>

          <div className="checkbox-container">
            <input
              checked={editCategoryData.available}
              onChange={(e) => {
                setEditCategoryData((prevData) => ({
                  ...prevData,
                  available: e.target.checked,
                }));
              }}
              className="checkbox-input"
              type="checkbox"
              id="customCheckbox"
            />
            <label htmlFor="customCheckbox" className="custom-checkbox"></label>
            <p className="checkbox-p">Available</p>
          </div>

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {deleteModal.type === "deleteCategory" && (
        <DeleteModal
          heading={"Delete category?"}
          paragraph={
            "This will delete all the categories and items that are inside of this category."
          }
          deleteFunction={loading === "deleteCategory" ? null : deleteCategory}
          setDeleteModal={setDeleteModal}
          btnText="Delete"
        ></DeleteModal>
      )}

      <h1 className="page-content-h">Menu Editor</h1>
      <p className="page-content-p">
        Edit your menu categories and items with ease.
      </p>

      <div className="menu-editor-container">
        <div className="menu-editor-items-div">
          {mainCategories.map((category) => {
            return (
              <div
                key={category.id}
                className="menu-editor-item-div"
                tabIndex="0"
              >
                <img
                  className="menu-editor-item-img"
                  src={category?.image?.url || noImage}
                />

                <div className="menu-editor-item-p-div">
                  <p className="menu-editor-item-p">
                    {category.name[languageList[0]]}
                  </p>
                </div>

                <div className="menu-editor-item-buttons">
                  <Link
                    to={`/${slug}/menu-editor/${category.id}`}
                    className="menu-editor-item-btn"
                  >
                    Enter
                  </Link>
                  <div
                    onClick={() => {
                      setActiveModal("editCategory");
                      setEditCategoryData({
                        ...category,
                        selectedNameLang: languageList[0],
                        selectedDescriptionLang: languageList[0],
                      });
                    }}
                    className="menu-editor-item-btn"
                  >
                    Edit
                  </div>
                  <div
                    onClick={() => {
                      setDeleteModal({
                        type: "deleteCategory",
                        id: category.id,
                      });
                    }}
                    className="menu-editor-item-btn"
                  >
                    Delete
                  </div>
                </div>
              </div>
            );
          })}

          <div className="menu-editor-add-item-div" tabIndex="0">
            <img className="menu-editor-add-img" src={add} />

            <div className="menu-editor-item-buttons">
              <div
                onClick={() => {
                  setActiveModal("addCategory");
                }}
                className="menu-editor-item-btn"
              >
                Add Category
              </div>
            </div>
          </div>
        </div>
      </div>
    </Navigation>
  );
};

export default MenuEditor;
